import { Form, Formik } from "formik";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { Container, Paper, Stack, Typography, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import { useAppSelector } from "@/hooks";
import { MediaManager } from "@/components";
import InputText from "@/components/InputText";
import PageHeader from "@/components/PageHeader";
import { useAddMenuMutation } from "@/features/organizations/api/menusApi";
import useSnackbar from "@/hooks/useSnackbar";
import routes from "@/constants/routes";

const FormSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, "Muy corto, ingresa al menos 3 caracteres")
    .max(50, "Muy largo, máximo 50 caracteres")
    .required("Campo requerido"),
  description: Yup.string()
    .min(10, "Muy corto, ingresa al menos 10 caracteres")
    .max(150, "Muy largo, máximo 150 caracteres")
    .required("Campo requerido"),
  image: Yup.array()
    .of(Yup.string())
    .min(1, "La foto de portada es requerida")
    .max(1)
    .required("Campo requerido"),
  order: Yup.number().optional(),
});

const CreateCatalog = () => {
  const [openSnackbar] = useSnackbar();
  const navigate = useNavigate();
  const container = useAppSelector((state) => state.ui?.container);
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  const [addMenu, { isLoading }] = useAddMenuMutation();
  const author = useAppSelector((state) => state.auth.user?.uid);

  const handleSubmit = async (values: any) => {
    const payload = {
      title: values.title,
      description: values.description,
      image: values.image,
      order: values.order ? Number(values.order) : 0,
      organization: organizationId,
      isPublished: false,
      createdBy: author,
    };

    const response = await addMenu({ draft: payload, organizationId });

    if (!response?.error && response?.data) {
      openSnackbar("Menú creado exitosamente", "success");
      const editPath = generatePath(routes.organizations.catalogo.editor, {
        organizationId,
        menuId: response.data.id,
      });
      navigate(editPath);
    } else {
      openSnackbar("Error al crear el menú", "error");
    }
  };

  return (
    <Stack spacing={3}>
      <PageHeader title="Crear Catálogo" />
      <Stack>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >
          <Formik
            initialValues={{
              title: "",
              description: "",
              image: [],
              order: "",
            }}
            validationSchema={FormSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <Paper sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ mb: 3 }}>
                      Detalles del Catálogo
                    </Typography>
                    <Stack spacing={3}>
                      <InputText label="Nombre" name="title" size="small" fullWidth />
                      <InputText
                        label="Descripción"
                        name="description"
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                      />
                      <InputText
                        label="Orden de visualización (Opcional)"
                        name="order"
                        size="small"
                        type="number"
                        fullWidth
                      />
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: 4, borderRadius: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="h4" sx={{ mb: 1 }}>
                        Foto de Portada
                      </Typography>
                      <MediaManager
                        value={values?.image[0]}
                        onChange={(value: string[]) => {
                          setFieldValue("image", value);
                        }}
                        helperText={errors?.image as string | undefined}
                      />
                    </Stack>
                  </Paper>

                  <Stack direction="row" spacing={2} justifyContent="flex-start" sx={{ px: 1 }}>
                    <Button
                      variant="outlined"
                      disabled={isLoading}
                      onClick={() => navigate(-1)}
                    >
                      Cancelar
                    </Button>
                    <LoadingButton
                      variant="contained"
                      color="success"
                      type="submit"
                      loading={isLoading}
                    >
                      Crear y Continuar
                    </LoadingButton>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Container>
      </Stack>
    </Stack>
  );
};

export default CreateCatalog;
